import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

export const useBrandUrl = (brandId: string | undefined, setSelectedBrand: any) => {
  const [brandUrl, setBrandUrl] = useState<string>('');
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch brand URL
  useEffect(() => {
    if (!brandId) {
      setLoading(false);
      return;
    }
    
    const fetchBrandUrl = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('brands')
          .select('url')
          .eq('id', brandId)
          .single();
          
        if (error) throw error;
        
        setBrandUrl(data.url || '');
      } catch (error) {
        console.error('Error fetching brand URL:', error);
        toast({
          title: 'Error',
          description: 'Failed to load brand URL',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrandUrl();
  }, [brandId]);

  // Update brand URL
  const updateBrandUrl = async (url: string) => {
    if (!brandId) return;

    try {
      const { error } = await supabase
        .from('brands')
        .update({
          url: url,
          default_landing_page: url,
          updated_at: new Date().toISOString()
        })
        .eq('id', brandId)
        .select()
        .single();

      if (error) throw error;

      // Update the local state
      setBrandUrl(url);

      // Update the selected brand context if it exists
      if (setSelectedBrand) {
        setSelectedBrand((prev: any) => prev ? {
          ...prev,
          url: url,
          default_landing_page: url
        } : prev);
      }

      toast({
        title: 'URL Updated',
        description: 'Brand URL has been updated successfully',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
    } catch (error) {
      console.error('Error updating brand URL:', error);
      toast({
        title: 'Error',
        description: 'Failed to update brand URL',
        variant: 'destructive',
      });
    }
  };

  // Re-analyze website content
  const reanalyzeWebsite = async () => {
    if (!brandId || !brandUrl) {
      toast({
        title: 'Error',
        description: 'Brand URL is required',
        variant: 'destructive',
      });
      return;
    }
    
    setIsReanalyzing(true);
    
    try {
      // Update status to show we're analyzing
      const { error: updateError } = await supabase
        .from('brands')
        .update({
          web_site_scraping_status: 'PROCESSING'
        })
        .eq('id', brandId);
        
      if (updateError) throw updateError;
      
      // Get session for API call
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      
      // Call the rescrape endpoint
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/web-site-scraper/rescrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          url: brandUrl
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: 'Website Analysis Started',
          description: 'The website is being analyzed to update your brand information. This may take a few minutes.',
          duration: 5000,
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });
      } else {
        throw new Error(result.error || result.details || 'Failed to start website analysis');
      }
    } catch (error) {
      console.error('Error re-analyzing website:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to re-analyze website',
        variant: 'destructive',
      });
      
      // Update status back to FAILED if there was an error
      const { error: updateError } = await supabase
        .from('brands')
        .update({
          web_site_scraping_status: 'FAILED'
        })
        .eq('id', brandId);
        
      if (updateError) {
        console.error('Error updating brand status:', updateError);
      }
    } finally {
      setIsReanalyzing(false);
    }
  };

  return {
    brandUrl,
    setBrandUrl,
    isReanalyzing,
    loading,
    updateBrandUrl,
    reanalyzeWebsite
  };
};

export default useBrandUrl;